import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  CircleNotch,
  CheckCircle,
  XCircle,
  WarningCircle,
  Info,
  ArrowClockwise,
} from "@phosphor-icons/react";

function formatDateTime(iso, tz = "Europe/Madrid") {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: tz,
    });
  } catch {
    return iso;
  }
}

const severityIcon = {
  info: { Icon: Info, color: "text-zinc-400" },
  warning: { Icon: WarningCircle, color: "text-orange-400" },
  error: { Icon: XCircle, color: "text-red-400" },
};

export default function Logs() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const e = await api.events(200);
      setEvents(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const i = setInterval(load, 15000);
    return () => clearInterval(i);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircleNotch size={32} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] text-zinc-500 tracking-[0.3em] uppercase font-mono mb-1">
            Auditoría
          </div>
          <h1 className="font-display font-black text-3xl tracking-tight">Registro de actividad</h1>
          <p className="text-sm text-zinc-400 mt-2">
            Últimos {events.length} eventos del sistema. Se actualiza automáticamente.
          </p>
        </div>
        <button
          data-testid="refresh-logs-button"
          onClick={load}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 rounded text-sm bg-zinc-900 border border-zinc-800 text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <ArrowClockwise size={14} className={refreshing ? "animate-spin" : ""} />
          Refrescar
        </button>
      </div>

      <div className="bg-[#121212] border border-zinc-800 rounded-md overflow-hidden">
        {events.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No hay eventos registrados.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-black/40 border-b border-zinc-800">
              <tr>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono w-44">
                  Timestamp
                </th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono w-44">
                  Tipo
                </th>
                <th className="text-left px-4 py-3 text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
                  Mensaje
                </th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const sev = severityIcon[e.severity] || severityIcon.info;
                return (
                  <tr key={e.id} className="border-b border-zinc-900 hover:bg-zinc-900/30">
                    <td className="px-4 py-2.5 text-xs font-mono text-zinc-500">
                      {formatDateTime(e.created_at)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-1.5 text-xs font-mono">
                        <sev.Icon size={14} className={sev.color} weight="bold" />
                        {e.event_type}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-zinc-200">{e.message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
